import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ImagePreview,
  TextField,
} from '../../../ui/core';
import { getDriverLicenseImage } from '../../../redux/Drivers/actions';

const PANEL_HEIGHT = 320;

// Shown when a driver's profile-photo commit returns `failed_match` /
// `no_face_detected`. Puts the just-uploaded photo (previewed locally from the
// selected file — the driver bucket is private, so there is no server URL to
// display) beside the driver's license so an admin can visually verify and
// override the automated check. Confirm re-commits with bypassVerification: true
// (handled by the parent's onConfirm, which threads through the same presign
// imageUrl the failed commit used).
//
// The license read distinguishes three right-panel states so a transient fetch
// error can't masquerade as "no license" and wrongly block a legitimate
// override: loading, loaded, error (retry), and none.
//
// "none" is a real, supported case, not a dead end: license documents live in
// Postgres and the Mongo mirror copies only the expiry date, so a driver can be
// fully licensed with no `licenseImageUrl` — and their own in-app selfie upload
// fails the automated match, making this override the only way to set a photo.
// The backend allows it (a driver with no stored license can still be set this
// way) and audits it, so instead of disabling Confirm we require the admin to
// type a reason, which is sent as `bypassReason` and logged with their admin id.
// The genuinely unknown states — loading, and a fetch error where we can't tell
// whether a license exists — stay disabled.
const ProfilePhotoBypassModal = ({ open, driverId, file, onConfirm, onClose }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [licenseState, setLicenseState] = useState('loading'); // loading | loaded | error | none
  const [licenseUrl, setLicenseUrl] = useState(null);
  const [bypassReason, setBypassReason] = useState('');
  const [committing, setCommitting] = useState(false);
  const [commitError, setCommitError] = useState(null);
  // Monotonic token so an in-flight license read that resolves after the modal
  // closes, or after a newer read started (rapid Retry / driverId change),
  // is ignored instead of setting state on an unmounted component or applying
  // a stale license.
  const loadSeq = useRef(0);

  // Preview the exact bytes the admin selected; revoke the object URL on unmount
  // or file change so it doesn't leak across open/close cycles.
  useEffect(() => {
    if (!file) {
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const loadLicense = useCallback(async () => {
    const seq = (loadSeq.current += 1);
    setLicenseState('loading');
    const { success, licenseImageUrl } = await getDriverLicenseImage(driverId)();
    // A newer load started or the modal unmounted while this was in flight.
    if (seq !== loadSeq.current) {
      return;
    }
    if (!success) {
      setLicenseState('error');
    } else if (!licenseImageUrl) {
      setLicenseState('none');
    } else {
      setLicenseUrl(licenseImageUrl);
      setLicenseState('loaded');
    }
  }, [driverId]);

  useEffect(() => {
    if (open && driverId) {
      loadLicense();
    }
    // Invalidate any in-flight read on unmount / driver change.
    return () => {
      loadSeq.current += 1;
    };
  }, [open, driverId, loadLicense]);

  // No license to compare against → the admin is vouching on their own
  // authority, so a reason is mandatory (and audited server-side).
  const reasonRequired = licenseState === 'none';
  const trimmedReason = bypassReason.trim();
  const confirmDisabled = licenseState === 'loading' || licenseState === 'error' || (reasonRequired && !trimmedReason);

  const handleConfirm = async () => {
    setCommitting(true);
    setCommitError(null);
    let success;
    let result;
    try {
      ({ success, result } = (await onConfirm(trimmedReason || undefined)) || {});
    } catch (error) {
      // Defensive: onConfirm resolves an outcome object rather than throwing,
      // but never leave the dialog stuck with Cancel disabled if it does.
      success = false;
    }
    setCommitting(false);
    if (success && result === 'set') {
      onClose();
    } else {
      setCommitError('The photo could not be set. Please try again.');
    }
  };

  const renderLicensePanel = () => {
    if (licenseState === 'loading') {
      return (
        <Stack alignItems="center" justifyContent="center" sx={{ height: PANEL_HEIGHT }} spacing={1}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">
            Loading license…
          </Typography>
        </Stack>
      );
    }
    if (licenseState === 'error') {
      return (
        <Stack alignItems="center" justifyContent="center" sx={{ height: PANEL_HEIGHT }} spacing={1}>
          <Typography variant="body2" color="text.secondary">
            Couldn&apos;t load the license.
          </Typography>
          <Button variant="outlined" onClick={loadLicense}>
            Retry
          </Button>
        </Stack>
      );
    }
    if (licenseState === 'none') {
      return (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{ height: PANEL_HEIGHT, backgroundColor: '#eaeaea', borderRadius: 1, padding: 2 }}
        >
          <Typography variant="body2" color="text.secondary" align="center">
            No license image on file — there is nothing to compare this photo against.
          </Typography>
        </Stack>
      );
    }
    return <ImagePreview id="bypass-license-photo" src={licenseUrl} height={PANEL_HEIGHT} />;
  };

  return (
    <Dialog
      open={open}
      onClose={committing ? undefined : onClose}
      maxWidth="md"
      fullWidth
      aria-label="Compare the uploaded photo with the driver's license"
    >
      <DialogTitle>{reasonRequired ? 'Set photo without a license check' : 'Verify photo against license'}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 2 }}>
          {reasonRequired
            ? // Nothing to compare against: say plainly that the admin, not a
              // check, is the authority here.
              'This driver has no license image on file, so there is nothing to compare the photo against. ' +
              'Confirming sets the photo as verified on your own authority — your admin id and the reason ' +
              'below are recorded.'
            : 'The uploaded photo didn’t match the driver’s license automatically. Compare the two and confirm ' +
              'only if they are the same person — this overrides the automated check and marks the driver eligible.'}
        </Typography>
        <Stack direction="row" spacing={2}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
              Uploaded photo
            </Typography>
            {previewUrl && <ImagePreview id="bypass-uploaded-photo" src={previewUrl} height={PANEL_HEIGHT} />}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
              Driver&apos;s license
            </Typography>
            {renderLicensePanel()}
          </Box>
        </Stack>
        {reasonRequired && (
          <TextField
            id="bypass-reason"
            label="Reason (required)"
            placeholder="How did you confirm this is the right person?"
            value={bypassReason}
            onChange={(event) => setBypassReason(event.target.value)}
            disabled={committing}
            fullWidth
            multiline
            minRows={2}
            sx={{ marginTop: 2 }}
          />
        )}
        {commitError && (
          <Typography variant="body2" color="error" sx={{ marginTop: 2 }}>
            {commitError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ padding: '16px 24px' }}>
        <Button variant="outlined" onClick={onClose} disabled={committing}>
          Cancel
        </Button>
        <Button
          color="accent"
          variant="contained"
          disableElevation
          loading={committing}
          disabled={confirmDisabled}
          onClick={handleConfirm}
        >
          Confirm &amp; set photo
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProfilePhotoBypassModal;
